<script>
import { defineComponent, inject, watch, computed, provide, ref, watchEffect } from 'vue'
import { tryOnScopeDispose } from '@vueuse/core'
import { useRoute, useRouter } from 'vue-router'
import { useQuery } from '@urql/vue'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { getGlobalFeatures, useI18nInjection } from '@/@monogrid/vue3-boost'
import { usePlatform } from '@monogrid/vue3-boost/functions'

import { preloaderInjection } from '@/main'
import SectionProduct from '@/organisms/SectionProduct.vue'
import CircleBtn from '@/atoms/CircleBtn.vue'
import ScrubbleVideo from '@/atoms/ScrubbleVideo.vue'
import BottomArrow from '@/assets/bottomArrow.svg'
import GetHomePage from '@/graphql/GetHomePage.gql'
import AnimatedLine from '@/atoms/AnimatedLine.vue'

export default defineComponent({
  name: 'HomePage',
  components: {
    BottomArrow,
    CircleBtn,
    ScrubbleVideo,
    SectionProduct,
    AnimatedLine
  },
  setup (props, { attrs, slots, emit }) {
    // refs
    const wrapper = ref()
    const video = ref()
    const metaLoaded = ref(false)
    const intro = ref()
    const route = useRoute()
    const router = useRouter()
    const platform = usePlatform()
    const animatedLine = ref()
    // const
    const preloader = inject(preloaderInjection)
    const { USE_I18N } = getGlobalFeatures()
    const Settings = inject('Settings')
    const sectionsProducts = []
    const sectionTriggers = []

    const stagger = 0.1
    // let
    let timeline
    let introTimeline
    // variants
    let locale
    if (USE_I18N) {
      const i18n = inject(useI18nInjection)()
      locale = i18n.locale
    }

    const scrollTo = (hash, time) => {
      const productName = hash.split('#')[1]
      const target = timeline.scrollTrigger.labelToScroll(productName)
      gsap.to(window, {
        scrollTo: { y: target },
        duration: time || Math.abs(window.scrollY - target) / 1000
      })
    }

    const setSectionsProducts = section => section && sectionsProducts.push(section)

    const setSectionTriggers = sections => {
      const threshold = 1.5
      sections.forEach((section, i) => {
        sectionTriggers.push({
          start: (section.startTime - threshold) / timeline.duration(),
          end: (section.endTime + threshold) / timeline.duration(),
          snap: (section.startTime + (section.endTime - section.startTime) / 2) / timeline.duration()
        })
      })
    }

    const snapTo = value => {
      let snap = value
      if (sectionTriggers.length > 0) {
        sectionTriggers.forEach((trigger) => { if (value < trigger.end && value > trigger.start) snap = trigger.snap })
      }
      return snap
    }

    const initTimeline = () => {
      const staggers = wrapper.value.querySelectorAll('.stagger').length
      const delay = stagger * staggers + 0.3
      introTimeline = gsap.timeline()
      introTimeline.fromTo(['.stagger'], { translateY: '100%', overflow: 'hidden' }, { translateY: '0%', overflow: 'inherit', ease: 'Power3.easeOut', duration: 1.5, stagger: stagger }, 0)
      introTimeline.fromTo(['.stagger-block'], { display: 'inline' }, { display: 'none', duration: 0.5, stagger: stagger }, 0)
      introTimeline.fromTo('.circle-btn ', { scale: 0 }, { scale: 1, duration: 1 }, delay)
      introTimeline.fromTo('.header', { autoAlpha: 0 }, { autoAlpha: 1, duration: 1 }, delay)
      introTimeline.fromTo('.footer', { autoAlpha: 0 }, { autoAlpha: 1, duration: 1 }, delay)
      animatedLine.value.animateLineEnter(introTimeline, delay + 0.2)

      timeline = gsap.timeline({
        scrollTrigger: {
          trigger: wrapper.value,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 1,
          snap: {
            snapTo,
            duration: { min: 0.1, max: 0.2 },
            delay: 0.5,
            ease: 'power1.inOut'
          }
        }
      })
      timeline.addLabel('init', 0)
      timeline.add(() => router.push({ hash: '', params: { skipScroll: true } }), 0)
      video.value.addAnimation(timeline, 0)
      timeline.fromTo(video.value.$el, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.1 }, 0.2)
      timeline.fromTo(intro.value, { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.5 }, 0.2)
      animatedLine.value.animateLineExit(timeline, 0.2)
      sectionsProducts.forEach((section, i) => {
        section.animateSection(timeline)
      })

      setSectionTriggers(HomePage.value.products)

      if (route.hash) scrollTo(route.hash, 1)
      ScrollTrigger.refresh()
      preloader.visible = resultHomePage.fetching.value
    }

    const resultHomePage = useQuery({
      query: GetHomePage,
      variables: computed(() => {
        return {
          status: 'PUBLISHED',
          variants: locale.value === 'en' ? 'base' : locale.value
        }
      })
    })

    const HomePage = computed(() => { return resultHomePage.data.value?.HomePage })
    const onLoaded = () => (metaLoaded.value = true)

    provide('HomePage', HomePage)

    watch(route, to => {
      if (!to.hash || to.params.skipScroll) return
      scrollTo(to.hash)
    })

    watchEffect(() => {
      if (!metaLoaded.value) return
      if (wrapper.value && !timeline) initTimeline()
    })

    tryOnScopeDispose(() => {
      if (timeline) timeline.kill()
      if (introTimeline) introTimeline.kill()
    })

    return {
      wrapper,
      animatedLine,
      mobile: platform.mobile,
      video,
      intro,
      HomePage,
      Settings,
      setSectionsProducts,
      onLoaded
    }
  }
})
</script>

<template>
  <div v-if="HomePage" ref="wrapper" class="wrapper">
    <ScrubbleVideo
      ref="video"
      class="scrubble-video fixed h-full w-full top-0 left-0 object-cover"
      :src="`/media/${mobile ? Settings.mobileVideo.id : Settings.bgVideo.id}`"
      @init.once="onLoaded"
    />
    <section ref="intro" class="intro relative flex flex-col items-center justify-center text-center smallHeight:mt-5">
      <div class="relative flex flex-col justify-center beforeMd:mb-4 md:mb-0">
        <h4 class="stagger event-name">
          <span class="c-gold">{{ HomePage.title }}</span>
          {{ HomePage.subtitle }}
        </h4>
        <div class="stagger-block"></div>
      </div>

      <div class="relative flex flex-col justify-center beforeMd:mb-1 md:mb-0">
        <h3 class="stagger">{{ HomePage.firstRow }}</h3>
        <div class="stagger-block"></div>
      </div>

      <div class="relative flex justify-center">
        <h1 class="stagger c-gold">
          {{ HomePage.secondRow }}
        </h1>
        <div class="stagger-block"></div>
      </div>

      <div class="relative flex justify-center sm:mb-24 md:mb-10 smallHeight:mb-6">
        <h1 class="stagger">
          {{ HomePage.thirdRow }}
        </h1>
        <div class="stagger-block"></div>
      </div>
      <AnimatedLine ref="animatedLine" />
      <CircleBtn
        :to="{ hash: '#MEDITERRANEO', params: { skipScroll: undefined } }"
        :dimension="'auto'"
        class="circle-btn smallHeight:mt-5"
      >
        <span class="c-gold label mb-1 smallHeight:mb-2">{{ HomePage.ctaLabel }}</span>
        <BottomArrow class="arrow md:absolute" />
      </CircleBtn>
    </section>
    <div ref="products" class="products" :style="{height: `${180*HomePage.products.length}vh`}">
      <SectionProduct
        v-for="(product, i) in HomePage.products"
        :key="i"
        v-bind="product"
        :ref="setSectionsProducts"
        :element="product"
        class="fixed top-0"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.sound-btn {
  z-index: $z-header;
  right: rem(60px);
  bottom: rem(60px);
}

.scrubble-video {
  z-index: $z-video;
  opacity: 0;
}

section {
  z-index: $z-content;
  height: 100vh;
}

h1 {
  line-height: 1.2;

  @screen smallHeight {
    line-height: 1;
  }
}

.event-name {
  position: relative;
  display: inline;
  padding: rem(4px) 0;

  &::after {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: rem(2px);
    background: $c-white;
    content: "";
  }

  @screen md {
    margin-bottom: rem(20px);
  }

  @screen smallHeight {
    margin-bottom: rem(10px);
  }
}

.arrow {
  width: 100%;
  max-width: rem(15px);
  color: $c-white;

  @screen md {
    bottom: rem(40px);
  }
}

.zoom-in--arrow {
  @screen md {
    bottom: rem(36px);
    transition: bottom 0.4s ease-in-out;
  }
}

.zoom-out--arrow {
  @screen md {
    bottom: rem(40px);
    transition: bottom 0.4s ease-in-out;
  }
}

.stagger {
  position: relative;
  overflow: hidden;
  transform: translateY(100%);

  @screen md {
    margin-bottom: rem(20px);
  }

  @screen smallHeight {
    margin-bottom: rem(10px);
  }
}

.stagger-block {
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: $c-dark-grey;
  transform: translateY(100%);
}

.line-progres--container {
  z-index: $z-video;
  width: 100%;
  height: 100%;
  color: $c-white;
}

.svg-loop-line {
  top: 0;
  left: 0;
  width: 100%;
  height: 95%;
  transform: translate(0%, 6%);
}

.loop-line {
  opacity: 0;
  stroke-dasharray: 371;
  stroke-dashoffset: 371;
}

</style>
